import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";
import { getCsrfToken } from "../../../public/api/authApi";
import {
  getChequeMasterData,
  getCheques,
  getChequeStats,
  updateChequeStatus,
} from "../api/chequesApi";
import ChequeSummaryWidgets from "../components/collection/ChequeSummaryWidgets";
import ChequeDirectorySection, {
  createInitialChequeColumnVisibility,
} from "../components/collection/ChequeDirectorySection";
import CreateChequeEntryForm from "../components/collection/CreateChequeEntryForm";
import ViewChequeModal from "../components/collection/ViewChequeModal";
import UpdateChequeStatusModal from "../components/collection/UpdateChequeStatusModal";
import {
  filterChequeStatuses,
  isChequeRowLocked,
} from "../config/chequeWorkspaceConfig";
import { useChequeRole } from "../utils/useChequeRole";
import { CHEQUE_ISSUES_SUBMODULE_SLUG } from "../utils/chequePermissionSlugs";

import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";

import ChequeMastersSection from "../components/control/ChequeMastersSection";

import { useLiveDashboardStats } from "../../../global/hooks/useLiveDashboardStats";
import "../styles/chequeDashboard.css";
import "../../user-builder/styles/userBuilder.css";

const ChequesWorkspacePage = () => {
  const perms = useChequeRole(CHEQUE_ISSUES_SUBMODULE_SLUG);
  const canEditActions = !perms.permissionsLoading && perms.canEdit;
  const canCreateActions = !perms.permissionsLoading && perms.canCreate;
  const showMasters = !perms.permissionsLoading && perms.canManageMasters;
  const canExport = !perms.permissionsLoading && perms.canExport;
  const excludeStatusNames = perms.canDelete ? [] : ["cancelled", "canceled"];
  const summaryVariant = perms.canDelete ? "admin" : "staff";

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);

  const [selectedViewChequeId, setSelectedViewChequeId] = useState(null);
  const [statusUpdateTarget, setStatusUpdateTarget] = useState(null);

  const formRef = useRef(null);
  const formSectionRef = useRef(null);

  const scrollToForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const [statsReloadToken, setStatsReloadToken] = useState(0);
  const [cheques, setCheques] = useState([]);
  const [loadingCheques, setLoadingCheques] = useState(true);

  const [columnVisibility, setColumnVisibility] = useState(
    createInitialChequeColumnVisibility,
  );

  const [filters, setFilters] = useState({
    search: "",
    bank_id: "",
    cheque_status_id: "",
    cheque_type_id: "",
    start_date: "",
    end_date: "",
    page: 1,
    limit: 10,
  });

  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  const [masterData, setMasterData] = useState({
    banks: [],
    types: [],
    statuses: [],
    users: [],
    payments: [],
  });

  const fetchMasterData = useCallback(async () => {
    try {
      const resp = await getChequeMasterData();
      setMasterData({
        banks: resp?.banks || [],
        types: resp?.types || [],
        statuses: resp?.statuses || [],
        users: [],
        payments: [],
      });
    } catch (err) {
      console.error("Failed to load master data: ", err);
    }
  }, []);

  const { dashboardStats, loadingStats, refreshStats } = useLiveDashboardStats({
    fetcher: getChequeStats,
    selectData: (resp) => resp?.data || resp,
    refreshDeps: [statsReloadToken],
  });

  const fetchChequesList = useCallback(async () => {
    setLoadingCheques(true);
    setErrorMsg("");
    try {
      const data = await getCheques(filters);
      setCheques(data?.cheques || []);
      if (data?.meta) {
        setPaginationMeta(data.meta);
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoadingCheques(false);
    }
  }, [filters]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChequesList();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [filters, fetchChequesList]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleEditCheque = (chequeId) => {
    scrollToForm();
    formRef.current?.loadAndEdit(chequeId);
  };

  const onStatusUpdateConfirm = async (newStatusId, clearanceDate) => {
    if (!statusUpdateTarget) return;

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await updateChequeStatus(
        statusUpdateTarget.id,
        newStatusId,
        clearanceDate,
        csrfToken,
      );

      setSuccessMsg("Cheque status updated successfully");
      setStatusUpdateTarget(null);
      fetchChequesList();
      setStatsReloadToken((t) => t + 1);
      refreshStats();
    } catch (err) {
      setErrorMsg("Error updating status: " + err.message);
    }
  };

  const formMasterData = useMemo(
    () => ({
      ...masterData,
      banks: filterActiveMasterRows(masterData.banks),
      types: filterActiveMasterRows(masterData.types),
      statuses: filterChequeStatuses(
        filterActiveMasterRows(masterData.statuses),
        excludeStatusNames,
      ),
    }),
    [masterData, excludeStatusNames],
  );

  const statusModalStatuses = useMemo(
    () =>
      filterChequeStatuses(
        filterActiveMasterRows(masterData.statuses),
        excludeStatusNames,
      ),
    [masterData.statuses, excludeStatusNames],
  );

  const QUICK_JUMP_ITEMS = [
    { key: "stats", label: "Statistics", targetId: "ochq-stats" },
    ...(canCreateActions ? [{ key: "add", label: "Add Entry", targetId: "ochq-add" }] : []),
    { key: "list", label: "Cheques List", targetId: "ochq-list" },
    ...(showMasters
      ? [{ key: "masters", label: "Master tables", targetId: "ci-masters" }]
      : []),
  ];

  return (
    <div className="ochq-shell">
      <header className="ochq-hero">
        <div className="ochq-hero__text">
          <p className="ochq-hero__eyebrow">Office Administration</p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <h2 className="ochq-hero__title" style={{ margin: 0 }}>
              Cheque issues
            </h2>
            <span
              className="ochq-page-tag"
              style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                padding: "0.2rem 0.65rem",
                borderRadius: "999px",
                background: showMasters ? "#e0f2fe" : "#fef3c7",
                color: showMasters ? "#0369a1" : "#b45309",
              }}
            >
              Cheque issues
            </span>
          </div>
          <p className="ochq-hero__sub">
            Cheque register, status updates, export, and master tables (banks, statuses, types) on one screen.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="ochq-layout">
        <div className="ochq-layout__main">
          <section id="ochq-stats" style={{ scrollMarginTop: "2rem" }}>
            <ChequeSummaryWidgets
              loading={loadingStats}
              dashboardStats={dashboardStats}
              variant={summaryVariant}
            />
          </section>

          {canCreateActions ? (
          <section id="ochq-add" style={{ scrollMarginTop: "2rem" }}>
            <div className="ochq-panel" ref={formSectionRef}>
              <div className="ochq-panel__header">
                <h2 className="ochq-panel__title">Cheque Entry</h2>
              </div>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#64748b",
                  margin: "-1rem 0 0.5rem",
                }}
              >
                Create a new cheque transaction or update an existing one.
              </p>
              <CreateChequeEntryForm
                ref={formRef}
                masterData={formMasterData}
                onCancel={() => scrollToForm()}
                onSuccess={(msg) => {
                  setSuccessMsg(msg || "Cheque entry saved successfully");
                  fetchChequesList();
                  setStatsReloadToken((t) => t + 1);
                  refreshStats();
                  scrollToForm();
                }}
              />
            </div>
          </section>
          ) : null}

          <section id="ochq-list" style={{ scrollMarginTop: "2rem" }}>
            <ChequeDirectorySection
              cheques={cheques}
              loading={loadingCheques}
              filters={filters}
              onFilterChange={setFilters}
              masterData={masterData}
              paginationMeta={paginationMeta}
              onPageChange={handlePageChange}
              onView={setSelectedViewChequeId}
              onEdit={canEditActions ? handleEditCheque : undefined}
              onChangeStatus={
                canEditActions
                  ? (row) =>
                      setStatusUpdateTarget({
                        id: row.id,
                        statusId: row.cheque_status_id,
                      })
                  : undefined
              }
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              canExport={canExport}
              isRowLocked={isChequeRowLocked}
            />
          </section>

          {showMasters ? (
            <div style={{ marginTop: "2.5rem" }}>
              <ChequeMastersSection onMastersChange={fetchMasterData} />
            </div>
          ) : null}
        </div>

        <GlobalQuickJump
          asideAriaLabel="Dashboard navigation"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      {selectedViewChequeId && (
        <ViewChequeModal
          chequeId={selectedViewChequeId}
          onClose={() => setSelectedViewChequeId(null)}
        />
      )}

      {statusUpdateTarget && (
        <UpdateChequeStatusModal
          open={!!statusUpdateTarget}
          onClose={() => setStatusUpdateTarget(null)}
          statuses={statusModalStatuses}
          currentStatusId={statusUpdateTarget.statusId}
          onConfirm={onStatusUpdateConfirm}
        />
      )}
    </div>
  );
};

export default ChequesWorkspacePage;
